import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import type { ReleaseNoteApp, ReleaseNoteTag } from '@/services/releaseNotes/releaseNotesApi'
import { formatDate } from '../utils/format'
import { sanitizeRichHtml } from '../utils/sanitizeHtml'
import { HTML_CONTENT_CLASSES } from './HtmlEditor'

interface ReleaseNoteCardProps {
  title: string
  subTitle?: string
  /** ISO timestamp; empty while the note has not been published. */
  publishTime?: string
  internal?: boolean
  apps: ReleaseNoteApp[]
  tags: ReleaseNoteTag[]
  /** HTML. */
  content: string
  /** Marks the note as one the reader has not seen before. */
  isNew?: boolean
}

/** A release note laid out as its readers see it. */
export function ReleaseNoteCard({
  title,
  subTitle,
  publishTime,
  internal,
  apps,
  tags,
  content,
  isNew,
}: ReleaseNoteCardProps) {
  const { t } = useTranslation()
  return (
    <article className="flex flex-col gap-2">
      <h2 className="flex items-start gap-2 text-xl font-semibold">
        {title || t('releaseNotes.untitled')}
        {isNew && (
          <Badge variant="blue" className="mt-1 shrink-0">
            {t('whatsNew.new')}
          </Badge>
        )}
      </h2>
      {subTitle && <p className="text-(--ad-text-muted)">{subTitle}</p>}
      <p className="text-xs text-(--ad-text-faint)">
        {publishTime ? formatDate(publishTime) : t('releaseNotes.notYetPublished')}
      </p>
      {(internal || tags.length > 0 || apps.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {internal && (
            <Badge variant="destructive">{t('releaseNotes.visibilities.INTERNAL')}</Badge>
          )}
          {tags.map((tag) => (
            <Badge key={tag} variant="success">
              {t(`releaseNotes.tagNames.${tag}`)}
            </Badge>
          ))}
          {apps.map((app) => (
            <Badge key={app} variant="blue">
              {t(`releaseNotes.appNames.${app}`)}
            </Badge>
          ))}
        </div>
      )}
      <div
        className={HTML_CONTENT_CLASSES}
        dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(content) }}
      />
    </article>
  )
}
